import useAddMediaToTimeline from '@src/hooks/useAddMediaToTimeline';
import { useEditor } from '@src/stores-v2';
import { useDatoClient } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { analytics } from '@src/utility/analytics';
import { deepClone } from '@src/utility/deepClone';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import ChatGPTService from '../../services/ChatGPTService';
import AIGenerateCard from '../common/AIGenerateCard';
import MusicSongItem from './MusicSettings/MusicSongItem';
import PrimaryActionButton from './PrimaryActionButton';
import SongCollection, { FIT_SONG_TO_VIDEO_DEFAULT } from './SongCollection';

import type { AIProducerCard } from '../../types.ts/general';
import type { Music, Song, Story } from '../../types.ts/story';
import type { AddSongToTrackOptions } from './SongCollection';

const MUSIC_SECTION_WIDTH_PX = 300;
const SONG_LIST_LENGTH_STEP = 5;

const isOnFirstPage = (songListLength: number): boolean => {
  return songListLength <= SONG_LIST_LENGTH_STEP;
};

type Props = {
  story?: Story;
  music?: Music[];
  selectedCard?: AIProducerCard;
};
const AIProducer = observer((props: Props) => {
  const videoCreator = useVideoCreatorStore();
  const datoClientStore = useDatoClient();
  const gptServiceRef = useRef(
    new ChatGPTService(videoCreator, datoClientStore.aiPromptRepository),
  );
  const gptService = gptServiceRef.current;
  const [currentSong, setCurrentSong] = useState('');
  const [selectedSongs, setSelectedSongs] = useState<Music['collection']>();
  const [songListLength, setSongListLength] = useState(SONG_LIST_LENGTH_STEP);

  const editorStore = useEditor();
  const { addMediaToTimeline } = useAddMediaToTimeline({
    editorStore,
    videoCreator,
  });

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const songList = selectedSongs || videoCreator.musicOptions;

  const preloadAudio = (url: string) => {
    const audio = new Audio(url);
    audio.preload = 'auto';
    audio.load();
  };

  const [isSelectedGenreLoading, setIsSelectedGenreLoading] =
    useState<boolean>(false);
  const callbackNameRef = useRef<string>();

  useEffect(() => {
    if (!isSelectedGenreLoading && callbackNameRef.current) {
      videoCreator.musicProducerLoading = false;
      callByName(callbackNameRef.current);
      callbackNameRef.current = undefined;
    }
  }, [isSelectedGenreLoading]);

  useEffect(() => {
    if (!songList) return;
    for (const song of songList) {
      preloadAudio(song.url);
      if (!videoCreator.audioTracksData[song.url]) {
        videoCreator.loadWaveformForSource({
          source: song.url,
          name: song.title,
        });
      }
    }
  }, [songList]);

  useEffect(() => {
    if (!isOnFirstPage(songListLength)) {
      selectSongsByMood();
    }
  }, [songListLength]);

  // const maxSongsDuration =
  //   songList?.reduce((acc, song) => {
  //     return Math.max(acc, Number(song.customData.duration.split(' ')[0]));
  //   }, 0) || 100;

  const getVideoDuration = () => {
    return videoCreator.duration;
  };

  const getAIMood = () => {
    const response =
      videoCreator?.contentStudioGeneratedContent?.['Music Selection']?.content
        ?.response || '';
    return (Array.isArray(response) ? response[0] : response) as string;
  };

  const selectedGenre: string = getAIMood().trim();

  const delayAndStopLoading = (
    callback: () => void,
    timeoutOuter: number = 3000,
    timeoutInner: number = 1000,
  ) => {
    setTimeout(() => {
      callback();
      setTimeout(() => {
        videoCreator.musicProducerLoading = false;
      }, timeoutInner);
    }, timeoutOuter);
  };

  const getMusicCollection = (): {
    storyLength: number;
    sortedCollection: Music['collection'];
    collection: Music['collection'];
  } => {
    const music = props.music?.find(
      m => m.genre.toLowerCase() === selectedGenre.toLowerCase(),
    );
    const collection = music?.collection || [];

    const storyLength = Number(getVideoDuration());

    const sortedCollection = deepClone(collection).sort(
      (a, b) => getDuration(b) - getDuration(a),
    );
    return { storyLength, sortedCollection, collection };
  };

  const hasMoreSongs = (): boolean => {
    const { collection } = getMusicCollection();
    return collection.length > songListLength && (songList?.length || 0) > 1;
  };

  const loadMoreSongs = () => {
    setSongListLength(songListLength + SONG_LIST_LENGTH_STEP);
  };

  const selectSongByMood = async () => {
    const { storyLength, sortedCollection } = getMusicCollection();
    if (!sortedCollection?.length) {
      videoCreator.musicOptions = [];
      setSelectedSongs([]);
      return;
    }

    let song = sortedCollection[0];
    videoCreator.musicProducerLoading = true;

    const last = sortedCollection[sortedCollection.length - 1];
    if (getDuration(last) >= storyLength) {
      delayAndStopLoading(() => {
        videoCreator.musicOptions = [last];
        setSelectedSongs([last]);
        handleAddToTrack(last, {
          fitSongToVideo: FIT_SONG_TO_VIDEO_DEFAULT,
        });
      });
      return;
    }

    if (getDuration(sortedCollection[0]) > storyLength) {
      for (let i = 1; i < sortedCollection.length; i++) {
        if (getDuration(sortedCollection[i]) < storyLength) {
          song = sortedCollection[i - 1];
          break;
        }
      }
    }

    delayAndStopLoading(() => {
      videoCreator.musicOptions = [song];
      setSelectedSongs([song]);
      handleAddToTrack(song, {
        fitSongToVideo: FIT_SONG_TO_VIDEO_DEFAULT,
      });
    });
  };

  const selectSongsByMood = async () => {
    const { collection } = getMusicCollection();

    if (!collection?.length) {
      videoCreator.musicOptions = [];
      setSelectedSongs([]);
      return;
    }

    videoCreator.musicProducerLoading = true;

    const songs = collection.slice(0, songListLength);

    delayAndStopLoading(
      () => {
        videoCreator.musicOptions = deepClone(songs);
        setSelectedSongs(deepClone(songs));
      },
      isOnFirstPage(songListLength) ? undefined : 500,
      isOnFirstPage(songListLength) ? undefined : 500,
    );
  };

  const callByName = (callbackName: string) => {
    switch (callbackName) {
      case 'selectSongByMood':
        selectSongByMood();
        break;
      case 'selectSongsByMood':
        selectSongsByMood();
        break;
      default:
        break;
    }
  };

  const getDuration = (collection: Music['collection'][0]) =>
    Number(collection?.customData?.duration?.split(' ')[0]) || 0;

  const handleAddToTrack = async (
    song: Song,
    options: AddSongToTrackOptions,
  ) => {
    const songDuration = parseFloat(song.customData?.duration);
    const songName = song.customData?.songName?.replace(/\b\w/g, match =>
      match.toUpperCase(),
    );
    addMediaToTimeline(
      {
        id: song.id,
        description: songName,
        src: song.url,
        title: songName,
        duration: songDuration,
      },
      'audio',
      {
        time: 0,
        fitToEnd: options?.fitSongToVideo || false,
      },
    );
  };

  const callAfterSelectedGenreLoaded = async (callbackName: string) => {
    try {
      analytics.track('generate_music', {
        storyId: videoCreator.story?.id,
        storyTitle: videoCreator.story?.title,
        videoId: videoCreator.currentVideo?.id,
        videoTitle: videoCreator.currentVideo?.title,
      });
      setIsSelectedGenreLoading(true);
      videoCreator.musicProducerLoading = true;
      callbackNameRef.current = callbackName;
      clearGeneratedMusicSelection();
      setSongListLength(SONG_LIST_LENGTH_STEP);
      await gptService.regenerateStreamResponse({
        key: 'Music Selection',
        setLoading: setIsSelectedGenreLoading,
      });
    } catch (error) {
      console.error("Couldn't generate mood for the story: ", error);
      callbackNameRef.current = undefined;
      videoCreator.musicProducerLoading = false;
      setIsSelectedGenreLoading(false);
    }
  };

  const clearGeneratedMusicSelection = () => {
    if (
      videoCreator?.contentStudioGeneratedContent?.['Music Selection']
        ?.hasBeenGenerated
    ) {
      videoCreator.contentStudioGeneratedContent[
        'Music Selection'
      ].hasBeenGenerated = false;
      videoCreator.contentStudioGeneratedContent['Music Selection'].content =
        null;
    }
  };

  const renderMusicGenerateOptions = () => (
    <>
      <audio ref={audioRef} preload="auto">
        <source src="" />
      </audio>

      <ButtonGroup>
        <AIGenerateCard
          handleClick={() => callAfterSelectedGenreLoaded('selectSongByMood')}
          text="Generate"
        />
        <AIGenerateCard
          handleClick={() => callAfterSelectedGenreLoaded('selectSongsByMood')}
          text="Identify Music Options"
        />
      </ButtonGroup>
      {renderGeneratedOptions()}
    </>
  );

  const renderCustomDescription = () => (
    <>
      <ContentDescription>
        <p className="description">
          <span className="bolded">
            Recommended Music based on length and emotion
          </span>
        </p>
        <div className="emotions">
          <span>Emotions identified</span>
          <div className="mood">
            {selectedGenre && <MoodTag>{selectedGenre}</MoodTag>}
          </div>
        </div>
      </ContentDescription>

      <HorizontalLine height="2" viewBox="0 0 300 2" fill="none">
        <path d="M0 1L300 1.00002" stroke="var(--border)" />
      </HorizontalLine>
    </>
  );

  const renderGeneratedOptions = () => (
    <Content>
      {songList && (
        <>
          {renderCustomDescription()}
          {songList?.length ? (
            <SongCollection onAddToTrack={handleAddToTrack}>
              {({ handleSongSelect, isSongSelected }) => (
                <>
                  {songList.map((song: Song) => (
                    <MusicSongItem
                      key={song.id}
                      audioRef={audioRef}
                      song={song}
                      currentSong={currentSong}
                      setCurrentSong={setCurrentSong}
                      mood={selectedGenre}
                      onSongClick={handleSongSelect}
                      customTimelineScale={
                        (MUSIC_SECTION_WIDTH_PX /
                          parseFloat(song.customData.duration)) *
                        1
                      }
                      isHighlighted={isSongSelected(song)}
                    />
                  ))}
                  {hasMoreSongs() && (
                    <LoadMoreSongsButton onClick={loadMoreSongs} />
                  )}
                </>
              )}
            </SongCollection>
          ) : (
            <div>No matching song in the album</div>
          )}
        </>
      )}
    </Content>
  );

  return <Main>{renderMusicGenerateOptions()}</Main>;
});

export default AIProducer;

const Main = styled.div``;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin: var(--spacing-md) 0 var(--spacing-sm);
`;

const Content = styled.div`
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
`;

const ContentDescription = styled.div`
  box-sizing: border-box;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-bold);
  display: flex;
  flex-direction: column;
  width: 320px;
  padding: 4px 10px 8px;

  gap: 10px;
  .description {
    margin: 6px 0;
    font-weight: 700;
  }

  .emotions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: 700;

    .mood {
      display: flex;
      gap: 5px;
    }
  }
`;

const MoodTag = styled.span`
  display: flex;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;
  border-radius: var(--radius-input);
  border: 1px solid var(--accent);
  color: var(--accent);
  font-size: 10px;
  font-weight: var(--font-weight-medium);
`;

const HorizontalLine = styled.svg`
  width: 100%;
`;

const LoadMoreButton = styled(PrimaryActionButton)`
  max-height: 40px;
  font-size: var(--text-sm);
  border: 1px solid var(--primary);
  color: var(--primary);
  background-color: var(--background);
  margin: 0 0 6px;
`;

// separate component to make sure disabled state is up-to-date
const LoadMoreSongsButton = observer(({ onClick }: { onClick: () => void }) => {
  const videoCreator = useVideoCreatorStore();
  return (
    <LoadMoreButton
      disabled={videoCreator.musicProducerLoading}
      onClick={onClick}
    >
      Find More Songs
    </LoadMoreButton>
  );
});
